import {
  SessionSettings,
  SessionStatus,
  Phrase,
  ClientSecret,
  RequestedData,
  Avatar,
  SessionReportAIAnalysis
} from '@facesignai/api'
import { DeviceDetailsT } from 'types/deviceDetails'
import { MaxmindCityT } from 'types/ipDetails'

export * from '@facesignai/api'

export interface SessionTemplate {
  id: string
  name: string
  createdAt: number
  settings: SessionSettings
}

export interface Attempt {
  clientSecret: ClientSecret
  startedAt: number
  conversation: string[]
  lastUserPhraseAppliedAt?: number
  lastAvatarPhraseAppliedAt?: number
  clientVersion?: string
  slackTs?: string
  isLocalhost?: boolean
  userVideoUrl?: string
  avatarVideoUrl?: string
  userVideoMimeType?: string
  avatarVideoMimeType?: string
}

export interface UserParam extends RequestedData {
  value?: string | null
}

export type PhraseObject = Phrase & {
  interruptedAt?: number
  moduleIndex?: number
  moduleCompleted?: boolean
  flags?: { key: string; value: string }[]
  stage?: string
  latencies?: Record<string, number>
}

export type EmailVerificationAttempt = {
  ts: string
  timestamp: number
  deliveredAt?: number
}

export type EmailVerificationData = {
  id: string
  sessionId: string
  code: string
  email: string
  createdAt: number
  slackNotifications?: Record<string, EmailVerificationAttempt>
}

export type ModuleData = {
  emailVerification?: {
    verificationId?: string
    userId?: string
    emailInputSkipped?: boolean
    livenessDetected?: boolean
    userName?: string
    isVerified?: boolean
    isNotNew?: boolean
    email?: string
  }
}

export interface SessionObject {
  id: string
  clientId: string
  apiKeyId: string
  createdAt: number
  startedAt?: number
  finishedAt?: number
  phrases: Record<string, PhraseObject>
  conversation: string[]
  status: SessionStatus
  moduleIndex: number
  settings: SessionSettings
  apiVersion?: string
  data: Record<string, string>
  usedClientSecrets: string[]
  clientSecret: ClientSecret
  attempts: Record<string, Attempt>
  slackTs?: string
  transcriptSlackTs?: string
  webhookUrl?: string
  modulesData?: ModuleData
  lang?: string
  slackNotificationsDisabled?: boolean
  aiAnalysisFirst?: SessionReportAIAnalysis
  aiAnalysis?: SessionReportAIAnalysis
  device?: DeviceDetailsT
  ip?: string
  location?: MaxmindCityT
}

export type OpenAIApiRes = {
  reply: string
  data?: { key: string; value: string }[]
  flags?: { key: string; value: boolean }[]
  endSession?: boolean
  digits?: number
}

export type ApiKey = {
  id: string
  key: string
  expireAt?: number
  createdAt: number
  enabled: boolean
}

export type Client = {
  id: string
  title: string
  createdAt: number
  deletedAt: number
  keys: string[]
  apiKeys: Record<string, ApiKey>
  domains?: string[]
  allowStandalone?: boolean
  debugInSlack?: boolean
  webhookUrl?: string
}

export type ControlT = {
  camera: boolean
  mic: boolean
}

export interface AuthUser {
  createdAt: string
  displayName?: string
  email: string
  lastLoginAt?: string
  phoneNumber?: string
  photoURL?: string
  uid: string
  [key: string]: unknown
}

export type StorageFileT = {
  url: string
  size: number
  contentType?: string
}

export namespace DBT {
  export enum INTERRUPT {
    'SMART' = 'smart',
    'REGULAR' = 'regular',
    'DISABLED' = 'disabled'
  }

  export enum TRANSCRIBER {
    DEEPGRAM = 'deepgram',
    GLADIA = 'gladia',
    SPEECHMATICS = 'speechmatics',
    GOOGLE = 'google',
    OPENAI = 'openai'
  }

  export enum AIPlatform {
    OPENAI = 'openai',
    GROQ = 'groq'
  }

  export interface SubPromptT {
    prompt: string
    jsonScheme: string
    temperature?: number
    aiPlatform?: AIPlatform
    models?: Record<AIPlatform, string>
  }

  export enum Demo2Stage {
    PHOTO = 'photo',
    NAME = 'name',
    REKOGNIZED_USER_GREETING = 'rekognizedUserGreeting',
    EMAIL_REQUEST = 'emailRequest',
    EMAIL_INPUT_SKIPPED = 'emailInputSkipped',
    EMAIL_SENT = 'emailSent',
    VERIFICATION = 'verification',
    VERIFICATION_SILENT = 'verificationSilent',
    USER_REPLY_ANALYSIS = 'userReplyAnalysis',
    DIGITS_EXTRACTOR = 'digitsExtractor'
  }

  export interface AIPromptConfT {
    prompt: string
    prompts?: Partial<Record<Demo2Stage, SubPromptT>>
    model?: string
    temperature: number
    maxTokens: number
    voiceId?: string
    jsonScheme?: string
    cannedResponse?: Record<string, string>
    cannedResponseTranslates?: Record<string, Record<string, string>>
    interruptionMode?: INTERRUPT
    initialPhrase?: string
    initialPhraseTranslates?: Record<string, string>
    finalPhrase?: string
    finalPhraseTranslates?: Record<string, string>
    transcriber?: TRANSCRIBER
    aiPlatform?: AIPlatform
    models?: Record<AIPlatform, string>
    avatarId?: string
    avatarQuality?: string
    directMode?: boolean
  }

  export interface AIConfTemplateT {
    id: string
    name: string
    createdAt: number
    conf: AIPromptConfT
  }

  export interface AICFQT {
    prompt: string
    model: string
    temperature: number
    maxTokens: number
  }

  export interface AIConfT {
    CFQ: AICFQT
    answerAnalysis: AIPromptConfT
    responseAnalysis: AIPromptConfT
    CFQSuggestions: AICFQT
  }

  export type ScreenshotT = {
    id: string
    createdAt: number
    url: string
    isLast: boolean
    isFirst: boolean
    faceId?: string
    livenessDetected?: boolean
    processed?: boolean
  }

  export type AvatarT = Avatar & {
    isDisabled?: boolean
    createdAt?: number
  }

  export type LangT = {
    id: string
    title: string
    createdAt: number
    isDisabled?: boolean
  }

  export type FaceT = {
    id: string
    createdAt: number
    url: string
  }

  export type VegasUserT = Record<string, unknown> & {
    id: string
    faces: Record<string, FaceT>
  }

  export type TestPhrase = {
    text: string
    isAvatar: boolean
    stage?: Demo2Stage
  }

  export type Test = {
    id: string
    createdAt: number
    title: string
    metadata: Record<string, unknown>
    userInfo: Record<string, unknown>
    conversation: TestPhrase[]
    validation: string
    isPassed?: boolean
    latency?: string
    aiResponse?: Record<string, unknown>
    lastTestTimestamp?: number
    images?: string[] //list of urls
    code?: string
  }

  export type HGAvatarT = {
    avatar_id: string
    created_at: number
    is_public: boolean
    status: string
  }
}
